import { useEffect, useMemo } from "react";
import _ from "lodash";
import { FetcherKeys } from "routes/dataroutes/Fetchers";
import { useFetcherData } from "v2/hooks/useFetcherData";
import { PaymentSource } from "types/billing/PaymentSource";
import { PaymentSourceActionData, PaymentSourceActions, PaymentSourceInputName } from "v2/dataroutes/BillingData";


export const PAYMENT_SOURCES_FETCHER_HREF = "/billing-data/payment-sources";

export function usePaymentSources() {
  const { value, fetcher, error, reload } = useFetcherData<PaymentSource[] | PaymentSourceActionData>({
    fetcherKey: FetcherKeys.PAYMENT_SOURCES,
    fetcherHref: PAYMENT_SOURCES_FETCHER_HREF,
  });

  // Normalize: loader returns PaymentSource[], action returns { paymentSources: PaymentSource[] }
  // Sort primary payment source to the top of the list
  const paymentSources = useMemo(() => {
    if (!value) return undefined;
    const sources = Array.isArray(value) ? value : value.paymentSources;
    // Sort: primary first, then cards before PayPal
    return _.orderBy(sources, ['isPrimary', (s) => s.type === 'card' ? 0 : 1], ['desc', 'asc']);
  }, [ value ]);

  useEffect(() => {
    if (fetcher?.state === "idle") {
      if (!paymentSources && error) {
        // fetcher preserved an error state, and its value is lost. have to reload
        reload();
      }
    }
  }, [ fetcher?.state, paymentSources, error, reload ]);


  const hasPayPalSource = useMemo(
    () => paymentSources?.some(s => s.type === "paypal") ?? false,
    [ paymentSources ]
  );

  const isSettingPrimary = fetcher?.state !== "idle" &&
    fetcher?.formData?.get(PaymentSourceInputName.action) === PaymentSourceActions.set_primary.value;

  return {
    paymentSources,
    paymentSourcesError: error,
    paymentSourcesFetcher: fetcher!,
    reloadPaymentSources: reload,
    hasPayPalSource,
    isSettingPrimary,
  };
}
